import type { RespuestaPublicar, ResultadoPublicacion } from './consultas'
import { formatearFecha } from './formato'
import { InsigniaEstadoPublicacion } from './insignias'
import { IconoCorrecto, IconoAviso } from '../../componentes/iconos'

/**
 * Que ha pasado con cada cliente. Uno por cliente y nunca un error global: en
 * una lista de veinte, "ha fallado" a secas no dice cuales se han creado, y
 * volver a publicar por si acaso es exactamente lo que crea el albaran de mas.
 *
 * Los cuatro finales no valen lo mismo:
 *
 * - `publicado` — pedido creado y evento en el calendario.
 * - `publicado_sin_evento` — **el pedido esta bien**. Solo falta el evento, que
 *   es secundario respecto al pedido. Se distingue en ambar, con su numero de
 *   presupuesto delante, para que nadie lo lea como un fallo y lo repita a mano.
 * - `duplicado` — ya habia un pedido de ese cliente ese dia y no se ha creado
 *   otro. Es la idempotencia haciendo su trabajo, no un error.
 * - `ambiguo` / `fallido` — no hay pedido. Estos si hay que arreglarlos.
 */

/** Los que no han terminado y tiene sentido volver a intentar. */
export const REINTENTABLES = new Set(['fallido', 'publicado_sin_evento'])

interface Props {
  respuesta: RespuestaPublicar
  reintentando: boolean
  onReintentar: () => void
}

function Fila({ resultado }: { resultado: ResultadoPublicacion }) {
  const conEvento = resultado.estado === 'publicado' && resultado.evento_id !== ''

  return (
    <li className="flex flex-col gap-1 border-b border-borde-suave px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-tinta">{resultado.nombre}</span>
        <InsigniaEstadoPublicacion estado={resultado.estado} />
        {resultado.presupuesto ? (
          <span className="text-sm text-tinta-2">Presupuesto {resultado.presupuesto}</span>
        ) : null}
        {/* Sin iconos de color solos: cada estado del evento lleva su palabra. */}
        <span className="text-xs text-tinta-apagada">
          {conEvento
            ? 'Evento de calendario creado'
            : resultado.estado === 'publicado'
              ? 'Sin identificador de evento'
              : resultado.estado === 'publicado_sin_evento'
                ? 'Falta el evento de calendario'
                : 'Sin evento'}
        </span>
      </div>
      {resultado.mensaje ? (
        <p className="text-sm text-tinta-apagada">{resultado.mensaje}</p>
      ) : null}
    </li>
  )
}

export function PanelResultados({ respuesta, reintentando, onReintentar }: Props) {
  const resultados = respuesta.resultados
  const cuenta = (estado: string) => resultados.filter((r) => r.estado === estado).length
  const pendientes = resultados.filter((r) => REINTENTABLES.has(r.estado))
  const creados = cuenta('publicado') + cuenta('publicado_sin_evento')
  const sinPublicar = cuenta('fallido') + cuenta('ambiguo')
  // Todo salió bien si hay al menos un albarán creado y nada quedó sin publicar.
  const todoOk = creados > 0 && sinPublicar === 0

  return (
    <section
      aria-label="Resultado de la publicacion"
      className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie"
    >
      {/* Encabezado claro del desenlace: que no quede duda de si se publicó. */}
      <div className="border-b border-borde px-4 pb-3 pt-4">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className={`grid size-7 shrink-0 place-items-center rounded-full ${
              todoOk ? 'bg-positivo-fondo text-positivo-tinta' : 'bg-atencion-fondo text-atencion-tinta'
            }`}
          >
            {todoOk ? <IconoCorrecto className="size-4" /> : <IconoAviso className="size-4" />}
          </span>
          <h2 className="text-sm font-semibold text-tinta">
            {todoOk
              ? creados === 1
                ? 'Albarán creado en Holded'
                : `${creados} albaranes creados en Holded`
              : 'Publicación con incidencias'}
          </h2>
        </div>
        <p className="mt-1.5 text-sm text-tinta-apagada">
          Publicación del {formatearFecha(respuesta.fecha_reparto)} · {cuenta('publicado')} publicados,{' '}
          {cuenta('publicado_sin_evento')} sin evento, {cuenta('duplicado')} ya existían,{' '}
          {sinPublicar} sin publicar.
        </p>
        {respuesta.aviso ? (
          <p className="mt-2 rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
            {respuesta.aviso}
          </p>
        ) : null}
      </div>

      <ul aria-label="Resultado por cliente" className="flex flex-col">
        {resultados.map((resultado) => (
          <Fila key={`${resultado.contact_id}-${resultado.presupuesto}`} resultado={resultado} />
        ))}
      </ul>

      {pendientes.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-borde px-4 py-3">
          <p className="text-sm text-tinta-apagada">
            Los presupuestos que ya existen no se vuelven a crear: reintentar es seguro.
          </p>
          <button
            type="button"
            disabled={reintentando}
            onClick={onReintentar}
            className="self-start rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:text-desactivado-tinta"
          >
            {reintentando
              ? 'Reintentando'
              : `Reintentar ${pendientes.length === 1 ? 'el cliente' : `los ${pendientes.length} clientes`} que no han terminado`}
          </button>
        </div>
      ) : null}
    </section>
  )
}
